import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BadgeCheck, Star, MapPin, Instagram, Heart, MessageCircle, Calendar,
  Sparkles, Check, ArrowLeft, Home, Building2, Video, ExternalLink, Camera, Hand,
  Award, ChevronLeft, ChevronRight, Share2,
} from 'lucide-react';
import { fetchArtist } from '../lib/api';
import { waLink, FILTER_PRODUCT_KITS, cleanImageList } from '../lib/constants';
import { useWishlist } from '../lib/useWishlist';
import { toast } from 'sonner';
import { trackEvent, trackArtistView } from '../lib/analytics';
import { ikImage, ikHero, ikThumb, ikVideo } from '../lib/imagekit';

const KIT_LABELS = Object.fromEntries(FILTER_PRODUCT_KITS.map(k => [k.value, k]));

const CANONICAL_OCCASIONS = ['Bridal', 'Engagement', 'Haldi / Mehendi', 'Roka', 'Party', 'Editorial', 'SFX'];

const PRICING_CATEGORIES = [
  { key: 'bridal',     label: 'Bridal',     desc: 'Full bridal makeup with hair, basic draping & touch-up kit included.' },
  { key: 'engagement', label: 'Engagement', desc: 'Refined engagement makeup with hair styling & basic draping.' },
  { key: 'party',      label: 'Party',      desc: 'Statement party makeup with hair styling for evening events.' },
  { key: 'airbrush',   label: 'Airbrush',   desc: 'Weightless airbrush base for camera-ready, second-skin finish.' },
  { key: 'sfx',        label: 'SFX',        desc: 'Editorial & special-effects makeup for shoots & conceptual looks.' },
];

const getDisplayCategories = (artist) => {
  const all = [...(artist.occasions || []), ...(artist.specializations || [])];
  const lc = all.map(s => s.toLowerCase());
  return CANONICAL_OCCASIONS.filter(c => {
    const k = c.toLowerCase();
    if (k === 'haldi / mehendi') return lc.some(x => x.includes('haldi') || x.includes('mehendi'));
    return lc.includes(k);
  });
};

export default function ArtistProfile() {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coverIdx, setCoverIdx] = useState(0);
  const [reelIdx, setReelIdx] = useState(0);
  const { has, toggle } = useWishlist();

  useEffect(() => {
    setLoading(true);
    fetchArtist(slug).then(setArtist).catch(() => setArtist(null)).finally(() => setLoading(false));
    trackArtistView(slug);
  }, [slug]);

  useEffect(() => {
    const n = cleanImageList(artist?.portfolio).length;
    if (!n) return;
    const t = setInterval(() => {
      setCoverIdx(i => (i + 1) % n);
    }, 4500);
    return () => clearInterval(t);
  }, [artist]);

  if (loading) {
    return <div className="pt-40 container-edge"><div className="h-[60vh] bg-[#f7f7f7] animate-pulse" /></div>;
  }
  if (!artist) {
    return (
      <div className="pt-40 container-edge text-center min-h-[60vh]" data-testid="artist-not-found">
        <h1 className="font-serif text-5xl">Artist not found</h1>
        <Link to="/artists" className="mt-6 inline-block text-[#eb5381] uppercase tracking-[0.25em] text-sm">Back to directory</Link>
      </div>
    );
  }

  const liked = has(artist.slug);
  const categories = getDisplayCategories(artist);
  const kit = KIT_LABELS[artist.product_kit] || KIT_LABELS.mixed;
  const isBridalArtist = categories.includes('Bridal');
  const cleanPortfolio = cleanImageList(artist.portfolio);
  const slides = cleanPortfolio.length ? cleanPortfolio : [artist.cover_image].filter(Boolean);
  const cp = artist.category_prices || {};
  // Only show real uploaded reels. If none, hide the reels viewer entirely.
  const reels = (artist.reels && artist.reels.length) ? artist.reels.map(src => ({ video: src })) : [];
  const hasReels = reels.length > 0;
  const currentReel = hasReels ? reels[reelIdx % reels.length] : null;
  const nextReel = () => setReelIdx(i => (i + 1) % reels.length);
  const prevReel = () => setReelIdx(i => (i - 1 + reels.length) % reels.length);

  const handleShare = async () => {
    const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
    trackEvent('share_profile_click', { artist_slug: artist.slug });
    const shareData = {
      title: `${artist.name} — FindYourMUA`,
      text: `Check out ${artist.name}, a verified makeup artist on FindYourMUA.`,
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Profile link copied to clipboard');
    } catch (err) {
      // user cancelled share or clipboard blocked — fail silently
      if (err && err.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Profile link copied to clipboard');
        } catch (_) { /* noop */ }
      }
    }
  };

  return (
    <div data-testid="artist-profile-page" className="bg-white">
      {/* Cover slideshow */}
      <section className="relative">
        <div className="relative h-[55vh] md:h-[70vh] overflow-hidden bg-neutral-100" data-testid="cover-slideshow">
          {slides.map((src, i) => (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${
                i === coverIdx ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={ikHero(src)}
                alt={`${artist.name} portfolio ${i + 1}`}
                className={`w-full h-full object-cover ${i === coverIdx ? 'animate-slow-zoom' : ''}`}
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/20" />

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCoverIdx(i)}
                aria-label={`Slide ${i + 1}`}
                data-testid={`cover-slide-dot-${i}`}
                className={`h-1 transition-all ${i === coverIdx ? 'w-10 bg-white' : 'w-5 bg-white/50'}`}
              />
            ))}
          </div>
        </div>

        <div className="container-edge -mt-16 sm:-mt-24 md:-mt-28 relative z-10">
          <div className="flex items-center justify-between gap-3">
            <Link to="/artists" data-testid="back-to-artists" className="inline-flex items-center gap-2 bg-white px-4 py-2 text-[11px] uppercase tracking-[0.25em] hover:text-[#eb5381]">
              <ArrowLeft className="w-3 h-3" /> All Artists
            </Link>
            <button
              type="button"
              onClick={handleShare}
              data-testid="share-profile-btn"
              className="inline-flex items-center gap-2 bg-white px-4 py-2 text-[11px] uppercase tracking-[0.25em] hover:text-[#eb5381]"
              aria-label="Share artist profile"
            >
              <Share2 className="w-3 h-3" /> Share Profile
            </button>
          </div>
          <div className="mt-6 bg-white p-5 sm:p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start border border-[#eaeaea] overflow-hidden">
            <div className="md:col-span-2 min-w-0 w-full">
              <div className="aspect-square overflow-hidden max-w-[120px] sm:max-w-[140px] md:max-w-none">
                <img src={ikThumb(artist.profile_image, 320)} alt={artist.name} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="md:col-span-6 min-w-0 w-full">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#eb5381]">
                <BadgeCheck className="w-3.5 h-3.5" /> Verified Artist
              </div>
              <h1 className="mt-2 font-serif text-[26px] sm:text-4xl md:text-6xl tracking-tight leading-[1.1] break-words">{artist.name}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[10px] sm:text-[11px] md:text-[12px] uppercase tracking-[0.12em] sm:tracking-[0.18em] md:tracking-[0.2em] text-neutral-600">
                <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 shrink-0" /> {artist.city}</span>
                <span className="inline-flex items-center gap-1.5" data-testid="profile-glam-score">
                  <Star className="w-3.5 h-3.5 fill-[#eb5381] text-[#eb5381] shrink-0" /> {artist.glam_score} Glam Score
                </span>
                {artist.years_experience && (
                  <span className="inline-flex items-center gap-1.5" data-testid="profile-experience">
                    <Award className="w-3.5 h-3.5 shrink-0" /> {artist.years_experience}+ Years
                  </span>
                )}
                {artist.instagram && (
                  <a href={`https://instagram.com/${artist.instagram}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#eb5381] break-all max-w-full">
                    <Instagram className="w-3.5 h-3.5 shrink-0" /> @{artist.instagram}
                  </a>
                )}
              </div>

              {artist.certifications && artist.certifications.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="profile-certifications">
                  {artist.certifications.map(c => (
                    <span key={c} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] border border-[#eaeaea] px-2.5 py-1 text-neutral-700 max-w-full">
                      <Award className="w-3 h-3 text-[#eb5381] shrink-0" />
                      <span className="break-words">{c}</span>
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-5 flex flex-wrap gap-2" data-testid="profile-categories">
                {categories.map(c => (
                  <span key={c} className="text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] bg-[#ffefd1] px-2.5 py-1 whitespace-nowrap">{c}</span>
                ))}
              </div>
            </div>
            <div className="md:col-span-4 min-w-0 w-full flex flex-col items-stretch gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Starting From</div>
                <div className="font-serif text-3xl">₹{artist.starting_price.toLocaleString('en-IN')}</div>
              </div>
              <div className="flex flex-row gap-2 w-full">
                <a
                  href={waLink(`Hi! I'd like to book ${artist.name} from ${artist.city}.`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="profile-whatsapp-cta"
                  onClick={() => trackEvent('whatsapp_click', { source: 'profile', artist_slug: artist.slug })}
                  className="flex-1 min-w-0 inline-flex items-center justify-center gap-2 bg-[#eb5381] hover:bg-[#d64570] text-white px-3 sm:px-4 py-3 text-[10px] sm:text-[11px] uppercase tracking-[0.15em] sm:tracking-[0.25em] transition-colors text-center"
                >
                  <MessageCircle className="w-4 h-4 shrink-0" /> <span className="truncate">Book on WhatsApp</span>
                </a>
                <button
                  onClick={() => toggle(artist.slug)}
                  data-testid="profile-wishlist-btn"
                  className={`w-12 shrink-0 grid place-items-center border ${liked ? 'border-[#eb5381] text-[#eb5381]' : 'border-black hover:bg-black hover:text-white'} transition-colors`}
                  aria-label="Save"
                >
                  <Heart className={`w-4 h-4 ${liked ? 'fill-[#eb5381]' : ''}`} />
                </button>
              </div>
              {isBridalArtist && artist.trial_available && (
                <a
                  href={waLink(`Hi ${artist.name}! I'd like to book a bridal trial session.`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="profile-book-trial-cta"
                  onClick={() => trackEvent('book_trial_click', { artist_slug: artist.slug })}
                  className="w-full inline-flex items-center justify-center gap-2 border border-[#eb5381] text-[#eb5381] hover:bg-[#eb5381] hover:text-white px-4 py-3 text-[10px] sm:text-[11px] uppercase tracking-[0.15em] sm:tracking-[0.25em] transition-colors"
                >
                  <Calendar className="w-4 h-4 shrink-0" /> Book a Trial
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="container-edge py-20 md:py-28 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-8">
          {/* About Artist */}
          <div data-testid="about-artist-section">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">About the Artist</div>
            <p className="mt-4 font-serif text-2xl md:text-3xl leading-snug tracking-tight text-balance">{artist.bio}</p>
          </div>

          {/* Portfolio + Reels (side by side, compact) */}
          <div className="mt-20" data-testid="portfolio-section">
            <div className="flex items-end justify-between mb-8 gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381] inline-flex items-center gap-2">
                  <Camera className="w-3.5 h-3.5" /> Portfolio &amp; Reels
                </div>
                <h3 className="mt-2 font-serif text-3xl md:text-4xl">The work, in living colour.</h3>
              </div>
              {artist.instagram && (
                <a
                  href={`https://instagram.com/${artist.instagram}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] hover:text-[#eb5381] whitespace-nowrap"
                >
                  See all on Instagram <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            <div className="grid grid-cols-12 gap-3 md:gap-4">
              {/* Photos: when no reels, take full 12 cols; otherwise 8 cols with reels on right */}
              <div
                className={`${hasReels ? 'col-span-12 md:col-span-8 grid grid-cols-3' : 'col-span-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4'} gap-2 md:gap-3`}
                data-testid="portfolio-grid"
              >
                {cleanPortfolio.slice(0, hasReels ? 6 : 8).map((src, i) => (
                  <div key={i} className="aspect-square img-zoom bg-[#f7f7f7]">
                    <img src={ikThumb(src, 600)} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ))}
              </div>

              {/* Reels: only render if there are real uploaded reels */}
              {hasReels && (
                <div className="col-span-12 md:col-span-4" data-testid="reels-viewer">
                  <div className="relative bg-black overflow-hidden aspect-[9/16]">
                    <video
                      key={reelIdx}
                      src={ikVideo(currentReel.video, 720, 70)}
                      controls
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    {reels.length > 1 && (
                      <>
                        <button
                          onClick={prevReel}
                          aria-label="Previous reel"
                          data-testid="reel-prev-btn"
                          className="absolute top-1/2 left-2 -translate-y-1/2 w-8 h-8 grid place-items-center bg-white/90 hover:bg-white text-black transition-colors z-10"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={nextReel}
                          aria-label="Next reel"
                          data-testid="reel-next-btn"
                          className="absolute top-1/2 right-2 -translate-y-1/2 w-8 h-8 grid place-items-center bg-white/90 hover:bg-white text-black transition-colors z-10"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                    <span>Reels</span>
                    <span data-testid="reel-counter">{reelIdx + 1} / {reels.length}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Techniques + Products + Included + Where */}
          <div className="mt-20 grid md:grid-cols-2 gap-12">
            <div data-testid="techniques-section">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Techniques</div>
              <ul className="mt-4 space-y-3">
                {(artist.techniques || []).map(t => (
                  <li key={t} className="flex items-center gap-2 text-base"><Check className="w-4 h-4 text-[#eb5381]" /> {t} Makeup</li>
                ))}
              </ul>
            </div>

            <div data-testid="products-section">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Products Used</div>
              <div className="mt-4 inline-flex items-center gap-2 bg-[#ffefd1] px-3 py-1.5 text-[11px] uppercase tracking-[0.2em]">
                <Sparkles className="w-3.5 h-3.5 text-[#eb5381]" />
                {kit.label}
              </div>
              <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{kit.note}</p>
            </div>

            <div data-testid="included-section">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Included</div>
              <ul className="mt-4 space-y-3 text-base">
                {artist.hairstyling && <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#eb5381]" /> Hairstyling</li>}
                {(artist.saree_draping || artist.dupatta_draping) && (
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#eb5381]" /> Basic Draping ({[artist.saree_draping && 'saree', artist.dupatta_draping && 'dupatta'].filter(Boolean).join(' & ')})</li>
                )}
                {isBridalArtist && artist.trial_available && (
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#eb5381]" /> Trial Session (for bridal)</li>
                )}
                {isBridalArtist && (
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#eb5381]" /> Touch-up Kit (for brides)</li>
                )}
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#eb5381]" /> Lashes</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#eb5381]" /> Coloured Lenses (on request)</li>
              </ul>
            </div>

            <div data-testid="where-section">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Where</div>
              <ul className="mt-4 space-y-3 text-base">
                {artist.studio_service && (
                  <li className="flex items-start gap-2">
                    <Building2 className="w-4 h-4 text-[#eb5381] mt-1 shrink-0" />
                    <div>
                      <div>Artist Studio</div>
                      {artist.studio_location && (
                        <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-0.5" data-testid="studio-location">
                          {artist.studio_location}
                        </div>
                      )}
                    </div>
                  </li>
                )}
                {artist.home_service && (
                  <li className="flex items-center gap-2"><Home className="w-4 h-4 text-[#eb5381]" /> Home Service</li>
                )}
                {artist.home_service && (
                  <li className="flex items-center gap-2"><Hand className="w-4 h-4 text-[#eb5381]" /> At Your Venue</li>
                )}
              </ul>
            </div>
          </div>

          {/* Pricing */}
          <div className="mt-20" data-testid="pricing-section">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Pricing</div>
            <h3 className="mt-2 font-serif text-3xl md:text-4xl">All categories, transparent prices.</h3>
            <p className="mt-3 text-sm text-neutral-600 max-w-xl leading-relaxed">
              Indicative starting prices for the most-booked services. Final quote depends on event date, venue, and customisation.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#eaeaea]" data-testid="pricing-grid">
              {PRICING_CATEGORIES.map(c => {
                const price = cp[c.key];
                const available = typeof price === 'number';
                return (
                  <div
                    key={c.key}
                    data-testid={`price-card-${c.key}`}
                    className={`bg-white p-6 lg:p-7 flex flex-col justify-between transition-colors ${available ? 'hover:bg-[#fff8ee]' : ''}`}
                  >
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">{c.label}</div>
                      {available ? (
                        <div className="mt-3 flex items-baseline gap-1.5">
                          <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">From</span>
                          <span className="font-serif text-2xl">₹{price.toLocaleString('en-IN')}</span>
                        </div>
                      ) : (
                        <div className="mt-3 font-serif text-xl text-neutral-400">On Request</div>
                      )}
                      <p className="mt-3 text-xs text-neutral-600 leading-relaxed">{c.desc}</p>
                    </div>
                    <a
                      href={waLink(`Hi! I'd like to enquire about ${artist.name}'s ${c.label} pricing.`)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`price-cta-${c.key}`}
                      className="mt-5 text-[10px] uppercase tracking-[0.25em] text-[#eb5381] hover:text-[#d64570] inline-flex items-center gap-1.5"
                    >
                      {available ? 'Book this' : 'Enquire'} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sticky availability */}
        <aside className="lg:col-span-4">
          <div className="sticky top-28 space-y-3">
            <div className="bg-[#ffefd1] p-8">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Availability</div>
              <h3 className="mt-2 font-serif text-3xl leading-tight">Reserve your date.</h3>
              <p className="mt-4 text-sm text-neutral-700 leading-relaxed">
                Calendar bookings open through our concierge. Share your date and we'll confirm availability within hours.
              </p>
              <div className="mt-6 flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-[#eb5381]" />
                <span>Open for 2026 &amp; 2027 dates</span>
              </div>
              <a
                href={waLink(`Hi! Checking ${artist.name}'s availability — please share open dates.`)}
                target="_blank"
                rel="noreferrer"
                data-testid="availability-whatsapp-cta"
                className="mt-7 inline-flex items-center justify-center gap-2 bg-[#eb5381] hover:bg-[#d64570] text-white w-full px-6 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Check on WhatsApp
              </a>
              {isBridalArtist && artist.trial_available && (
                <a
                  href={waLink(`Hi! I'd like to book a bridal trial with ${artist.name}.`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="availability-trial-cta"
                  className="mt-3 inline-flex items-center justify-center gap-2 border border-[#eb5381] text-[#eb5381] hover:bg-[#eb5381] hover:text-white w-full px-6 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors"
                >
                  <Calendar className="w-4 h-4" /> Book a Trial
                </a>
              )}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
